import React, { Fragment, useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Trans } from "@lingui/macro";

import { UserIcon, AVATAR_SIZE } from "widgets/datadisplay";
import { Popover, POPOVER_POSITIONS } from "widgets/popover";
import { LazyLoadComponent } from "widgets/lazyloader";
import { Menu, SubMenu, MenuDivider, MenuItem } from "widgets/menu";
import { Link } from "widgets/navigation";
import { hasFeatures } from "widgets/others";

import { useAccountStore, useUserStore } from "@/common/libs/stores";
import { clearDelegationData } from "@/common/libs/utils";
import {
  USER_TYPES,
  PRODUCTS,
  ACCOUNT_TYPES,
  EXTENDED_MANAGEMENT_FEATURE_FLAGS,
  ROLES,
  ANALYTICS_FEATURE_FLAGS,
  PRODUCT_TYPES
} from "@/common/constants/index.js";
import { DataService } from "@/common/libs/services/index.js";
import { Classes } from "../../styles/classes";

import styles from "./topnav.css";

import LogoIcon from "./logo.svg";
import QuestionMarkIcon from "./question-mark.svg";
import QuestionMarkColorIcon from "./question-mark-color.svg";

const ALLOW_FEATURES = {
  ALLOW_STATS_MODULE: "community_allow_simple_stats",
  ALLOW_PROCESS_MODULE: "process_allow_process_module"
};

const CHAMPION_URL = "community.kissflow.com";

export function CommonTopnav() {
  const accountId = useAccountStore.getState()._id;
  const currentUser = useUserStore((state) => state.currentUser);
  let { AccountDetails: accountDetails } = currentUser;
  const companyLogo = useAccountStore((state) => state.CompanyLogo);
  const { isEmployeeAdmin, isExtendedUserManagement, isMemberAdmin } =
    useAccountStore((state) => ({
      isEmployeeAdmin: state.isEmployeeAdmin,
      isExtendedUserManagement: state.isExtendedUserManagement,
      isMemberAdmin: state.isMemberAdmin
    }));
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const currentClosePopup = useRef(null);
  const [isTopnavReady, setIsTopnavReady] = useState(false);
  const [isThemeOpen, setThemeOpen] = useState(false);
  const [hasProductUpdate, setProductUpdate] = useState(false);

  useEffect(function GettingTopNavContainer() {
    if (document.getElementById("topnavcontainer")) {
      setIsTopnavReady(true);
    }
  }, []);

  useEffect(function updatingCannonBadge() {
    let cannyWidget = document.getElementById("cannyWidget");
    const updateCannyBadge = () => {
      console.info("********* Update Canny Badge called  ***********");
      let cannyBadge = document.querySelector(
        "#cannyWidget .Canny_BadgeContainer .Canny_Badge"
      );
      if (cannyBadge) {
        setProductUpdate(true);
      }
    };
    cannyWidget &&
      cannyWidget.addEventListener("DOMNodeInserted", updateCannyBadge);
    return () =>
      cannyWidget &&
      cannyWidget.removeEventListener("DOMNodeInserted", updateCannyBadge);
  }, []);

  const openIntercom = function openIntercom() {
    currentClosePopup.current && currentClosePopup.current();
    window.Intercom("show");
  };

  // const openCanny = () => {
  //   currentClosePopup.current && currentClosePopup.current();
  //   document.querySelector("#cannyWidget > div").click();
  //   setProductUpdate(false);
  // };

  const closeThemeModal = (e) => {
    e.stopPropagation();
    setThemeOpen(false);
  };

  const isIntegrationEnabled = () => {
    const flowControl = currentUser.AccountDetails.FlowControl;
    return !(
      flowControl &&
      flowControl[PRODUCTS.INTEGRATION] &&
      flowControl[PRODUCTS.INTEGRATION].IsDisabled
    );
  };

  const isDeveloperPlatformEnabled = () => {
    return (
      useAccountStore.getState().AccountType >=
        ACCOUNT_TYPES.DEVELOPMENT_ACCOUNT &&
      useAccountStore.getState().UserDetails.UserSystemType >=
        USER_TYPES.SUPER_ADMIN
    );
  };

  const isAnalyticsPreferenceEnabled = useMemo(
    function analyticsPreference() {
      return (
        currentUser.AccountDetails.AnalyticsPreference &&
        currentUser.AccountDetails.AnalyticsPreference.Status === "Active"
      );
    },
    [currentUser]
  );

  const isAnalyticsEnabled = () => {
    const isKPCProduct =
      accountDetails.LicenseInfo.Product === PRODUCT_TYPES.KF_KPC;
    return (
      isKPCProduct &&
      ((hasFeatures([ANALYTICS_FEATURE_FLAGS.REPORT_CREATE_TABLE]) &&
        isAnalyticsPreferenceEnabled) ||
        (hasFeatures([ANALYTICS_FEATURE_FLAGS.REPORT_CREATE_TABLE]) &&
          !isAnalyticsPreferenceEnabled &&
          currentUser.UserType === ROLES.SUPER_ADMIN))
    );
  };

  const isChannelEnabled = () => {
    return !currentUser?.AccountDetails?.FlowControl?.Channel?.IsDisabled;
  };

  function handleLogout() {
    window.fb?.signout();
    clearDelegationData();
    document.location.href = `/signin/2/${accountId}/logout`;
  }

  const handleComunityRequest = () => {
    let [championReq] = DataService.get(`https://${CHAMPION_URL}/id`, {
      isCORS: true
    });
    championReq()
      .then((data) => {
        if (data.UserDetails?.status === "error") {
          document.body.style.cursor = "progress";
          const { _id } = useAccountStore.getState();
          const [sigininSSOCall] = DataService.get(
            `/signin/2/${_id}/sso/${data._id}?sso_domain=${CHAMPION_URL}`
          );

          sigininSSOCall()
            .then((res) => {
              window.open(res.RedirectURL, "_blank");
            })
            .catch((err) => {
              window.open(`https://${CHAMPION_URL}`, "_blank");
            });
        } else {
          window.open(`https://${CHAMPION_URL}`, "_blank");
        }
      })
      .catch((error) => {
        window.open(`https://${CHAMPION_URL}`, "_blank");
      })
      .finally(() => {
        document.body.style.cursor = "default";
      });
  };

  const setPreviousRoute = () => {
    if (
      !["/view/mysettings", "/view/account"].some((path) =>
        window.location.pathname.includes(path)
      )
    ) {
      window._previousRoute = window.location.pathname;
    }
  };

  return (
    isTopnavReady &&
    createPortal(
      <Fragment>
        <div className={styles.logoContainer}>
          <Link route={"/"}>
            <LogoIcon width={32} height={32} />
          </Link>
        </div>
        <div style={{ flex: "1 1 100%" }} id="navmenucontainer">
          <div className={styles.navgroups} />
        </div>
        <div className={styles.rightColumn}>
          <div
            id="workflowupgradenavcontainer"
            className={styles.upgradenavcontainer}
          />
          <div id="searchnavcontainer" className={styles.searchnav} />
          <div className={styles.helpnav}>
            <Popover
              position={POPOVER_POSITIONS.BOTTOM_RIGHT}
              destroyOnClose={false}
              targetRenderer={({ closePopup }) => {
                currentClosePopup.current = closePopup;
                return (
                  <div
                    className={`${styles.helpnavbutton} ${
                      hasProductUpdate
                        ? styles.hasProductUpdate
                        : styles.hasNoProductUpdate
                    }`}
                  >
                    {hasProductUpdate ? (
                      <QuestionMarkColorIcon />
                    ) : (
                      <QuestionMarkIcon />
                    )}
                  </div>
                );
              }}
            >
              <Menu>
                <MenuItem
                  className={styles.profilemenuitem}
                  onClick={openIntercom}
                  data-heaplocator="navSupport"
                >
                  <Link className={Classes.base}>
                    <Trans>Support</Trans>
                  </Link>
                </MenuItem>
                <MenuItem
                  className={styles.profilemenuitem}
                  data-heaplocator="navDocumentation"
                >
                  <a
                    href="https://helpdocs.kissflow.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Trans>Documentation</Trans>
                  </a>
                </MenuItem>
                <MenuItem
                  className={styles.profilemenuitem}
                  data-heaplocator="navCustomerCommunity"
                >
                  <div
                    className={styles.menuItem}
                    onClick={handleComunityRequest}
                  >
                    <Trans>Ask the community</Trans>
                  </div>
                </MenuItem>
                {/* NOTE: Disabling product updates menu as per Nandhini request since product updates not loading */}
                {/* <MenuItem
                  className={styles.profilemenuitem}
                  data-heaplocator="navProductUpdates"
                  onClick={openCanny}
                >
                  <div className={styles.productUpdate}>
                    <Trans>Product updates</Trans>
                    {hasProductUpdate && (
                      <div className={styles.productUpdateBadge}>
                        <StatusChip
                          color={STATUS_CHIP_COLOR.PURPLE}
                          type={STATUS_CHIP_TYPE.LITE}
                          size={CHIP_SIZES.SMALL}
                        >
                          <Trans>New</Trans>
                        </StatusChip>
                      </div>
                    )}
                  </div>
                </MenuItem> */}
                <MenuItem
                  className={styles.profilemenuitem}
                  data-heaplocator="navAcademy"
                >
                  <a
                    href="https://academy.kissflow.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Trans>Academy</Trans>
                  </a>
                </MenuItem>
              </Menu>
            </Popover>
          </div>
          <div id="notificationavcontainer" className={styles.notificationav} />
          {/** will be used for showing upgrade buttong */}
          <div
            id="upgradenavcontainer"
            className={styles.upgradenavcontainer}
          />
          <div className={styles.companyLogo}>
            {companyLogo && (
              <img
                className={styles.companyLogoImg}
                src={companyLogo.url}
                alt={"Company Logo"}
              />
            )}
          </div>
          <div className={styles.profile} id="profile">
            <Popover
              className={styles.profilemenu}
              position={POPOVER_POSITIONS.BOTTOM_RIGHT}
              destroyOnClose={false}
              isOpen={profileMenuOpen}
              targetRenderer={({ closePopup }) => {
                currentClosePopup.current = closePopup;
                return (
                  <div style={{ cursor: "pointer" }}>
                    <UserIcon
                      userId={currentUser._id}
                      name={currentUser.Name}
                      size={AVATAR_SIZE.SIZE36}
                    />
                  </div>
                );
              }}
            >
              <Menu
                onChange={() => {
                  setProfileMenuOpen(false);
                  currentClosePopup.current && currentClosePopup.current();
                }}
              >
                {/* {currentUser.UserSystemType > USER_TYPES.USER && (
                  <MenuDivider />
                )} */}
                {isExtendedUserManagement && (
                  <MenuItem className={styles.profilemenuitem}>
                    <Link
                      className={Classes.base}
                      route={`/account/user/profile/${currentUser._id}`}
                    >
                      <Trans>My profile</Trans>
                    </Link>
                  </MenuItem>
                )}
                <MenuItem
                  className={styles.profilemenuitem}
                  onMouseDown={setPreviousRoute}
                >
                  <Link
                    className={Classes.base}
                    route={`/mysettings/generalsettings`}
                  >
                    <Trans>My settings</Trans>
                  </Link>
                </MenuItem>
                {hasFeatures([ALLOW_FEATURES.ALLOW_STATS_MODULE]) &&
                  hasFeatures([ALLOW_FEATURES.ALLOW_PROCESS_MODULE]) &&
                  currentUser.UserSystemType > USER_TYPES.USER && (
                    <MenuItem className={styles.profilemenuitem}>
                      <Link
                        className={Classes.base}
                        route="/home/pendingactions"
                      >
                        <Trans>My tasks</Trans>
                      </Link>
                    </MenuItem>
                  )}
                <MenuItem
                  className={styles.profilemenuitem}
                  onClick={() => setThemeOpen(true)}
                >
                  <div className={`${Classes.base} ${styles.menuItem}`}>
                    <Trans>Themes</Trans>
                  </div>
                  <LazyLoadComponent
                    component={() =>
                      import(/* webpackPrefetch: true */ "account/themeModal")
                    }
                    componentName="Theme"
                    open={isThemeOpen}
                    onClose={closeThemeModal}
                  />
                </MenuItem>
                {isIntegrationEnabled() && (
                  <MenuItem className={styles.profilemenuitem}>
                    <Link
                      className={Classes.base}
                      route="/integration/integrations"
                    >
                      <Trans>Integrations</Trans>
                    </Link>
                  </MenuItem>
                )}
                {isDeveloperPlatformEnabled() && (
                  <SubMenu
                    title={
                      <div>
                        <Trans>Developer platform</Trans>
                      </div>
                    }
                  >
                    {isIntegrationEnabled() && (
                      <MenuItem className={styles.profilemenuitem}>
                        <Link
                          className={Classes.base}
                          route="/integration/connector"
                        >
                          <Trans>Connector builder</Trans>
                        </Link>
                      </MenuItem>
                    )}
                    <MenuItem className={styles.profilemenuitem}>
                      <Link
                        className={Classes.base}
                        route="/developerplatform/appstore"
                      >
                        <Trans>App Store builder</Trans>
                      </Link>
                    </MenuItem>
                  </SubMenu>
                )}

                {/* <SubMenu title="Theme" className={styles.themestyle}>
                <MenuItem
                  onClick={() => {
                    switchTheme("auto");
                  }}
                >
                  Auto
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    switchTheme("light");
                  }}
                >
                  Light
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    switchTheme("dark");
                  }}
                >
                  Dark
                </MenuItem>
              </SubMenu> */}
                {isExtendedUserManagement && (
                  <Fragment>
                    <MenuDivider />
                    {hasFeatures([ALLOW_FEATURES.ALLOW_STATS_MODULE]) &&
                      hasFeatures([ALLOW_FEATURES.ALLOW_PROCESS_MODULE]) &&
                      currentUser.UserSystemType > USER_TYPES.USER && (
                        <MenuItem className={styles.profilemenuitem}>
                          <Link className={Classes.base} route="/home/explorer">
                            <Trans>Back office</Trans>
                          </Link>
                        </MenuItem>
                      )}
                    {currentUser.UserSystemType > USER_TYPES.USER &&
                      hasFeatures([ALLOW_FEATURES.ALLOW_STATS_MODULE]) &&
                      isChannelEnabled() && (
                        <MenuItem className={styles.profilemenuitem}>
                          <Link
                            className={Classes.base}
                            route="/channel/analytics"
                          >
                            <Trans>Analytics</Trans>
                          </Link>
                        </MenuItem>
                      )}
                    {(isEmployeeAdmin ||
                      (currentUser.UserSystemType === USER_TYPES.SUPER_ADMIN &&
                        hasFeatures([
                          EXTENDED_MANAGEMENT_FEATURE_FLAGS.ADMIN_ALLOW_EMPLOYEE_MANAGEMENT
                        ]))) && (
                      <MenuItem className={styles.profilemenuitem}>
                        <Link
                          className={Classes.base}
                          route="/account/employeemanagement/employees"
                        >
                          <Trans>Employee management</Trans>
                        </Link>
                      </MenuItem>
                    )}
                    {(isMemberAdmin ||
                      (currentUser.UserSystemType === USER_TYPES.SUPER_ADMIN &&
                        hasFeatures([
                          EXTENDED_MANAGEMENT_FEATURE_FLAGS.ADMIN_ALLOW_MEMBER_MANAGEMENT
                        ]))) && (
                      <MenuItem className={styles.profilemenuitem}>
                        <Link
                          className={Classes.base}
                          route="/account/membermanagement/members"
                        >
                          <Trans>Member management</Trans>
                        </Link>
                      </MenuItem>
                    )}
                  </Fragment>
                )}
                {currentUser.UserSystemType > USER_TYPES.USER && (
                  <MenuItem
                    className={styles.profilemenuitem}
                    onMouseDown={setPreviousRoute}
                  >
                    <Link className={Classes.base} route="/account/admin">
                      <Trans>Account administration</Trans>
                    </Link>
                  </MenuItem>
                )}
                <MenuDivider />
                {isAnalyticsEnabled() && (
                  <>
                    <MenuItem className={styles.profilemenuitem}>
                      <Link className={Classes.base} route="/analytics">
                        <Trans>Analytics</Trans>
                      </Link>
                    </MenuItem>
                    <MenuDivider />
                  </>
                )}
                <MenuItem className={styles.profilemenuitem}>
                  <Link
                    className={Classes.base}
                    target="_self"
                    onClick={handleLogout}
                  >
                    <Trans>Sign out</Trans>
                  </Link>
                </MenuItem>
              </Menu>
            </Popover>
          </div>
        </div>
      </Fragment>,
      // <div className={Classes.h3}>common top nav</div>,
      document.getElementById("topnavcontainer")
    )
  );
}
